import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { DndContext, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { useSalon } from "../context/SalonContext";
import api from "../lib/api";
import { returnHomeIfRequested } from "../lib/setupRoute";
import { maskPhone } from "../lib/format";
import ConflictResolutionModal, { findNextFreeSlot } from "../components/conflict-resolution-modal";
import ImageCropperModal from "../components/ImageCropperModal";

/**
 * Collaborators — Painel de gestão do PROFISSIONAL (não do cliente).
 * Estética dark com apenas contorno dourado sutil.
 * Pílulas (abas):
 *   1. FINANCEIRO DO COLABORADOR
 *   2. CADASTRO DO COLABORADOR (com gráfico financeiro 6 períodos)
 *   3. AGENDA SEMANAL (Dom-Sáb, navegação ±6 meses, CRUD de agendamentos)
 */
const Collaborators = () => {
    const { professionals, upsertProfessional, setActiveModule, syncTenantData } = useSalon();
    // view: 'hub' (grid de cards) | 'detail' (uma única página com Cadastro + Financeiro + Agenda)
    const [view, setView] = useState("hub");
    const [activeProId, setActiveProId] = useState(null);
    const [newProOpen, setNewProOpen] = useState(false);
    // Senha temporária recém-gerada (create) — casada por e-mail, exibida uma
    // única vez na Seção 1. Nunca fica guardada.
    const [provisioned, setProvisioned] = useState(null); // { email, password }

    const pro = professionals.find((p) => p.id === activeProId) || null;

    const handleNewProCreate = async (data) => {
        const newId = upsertProfessional(data, {
            onSaved: (res) => {
                if (res?.temp_password) {
                    setProvisioned({ email: (res.email || data.email || "").toLowerCase(), password: res.temp_password });
                    // reconcilia o id local -> uuid do backend p/ habilitar "gerar nova senha"
                    syncTenantData?.();
                }
                if (res?.access_sent) {
                    toast.success("Acesso ao app enviado", {
                        description: `Senha temporária enviada por WhatsApp para ${data.phone}.`,
                    });
                } else if (data.email && data.phone) {
                    toast.warning("Colaborador cadastrado — senha na tela, mas o WhatsApp não confirmou", {
                        description: "Verifique se o WhatsApp do salão está conectado e o telefone do colaborador está correto.",
                    });
                }
            },
        });
        setNewProOpen(false);
        toast.success("Colaborador cadastrado", { description: data.name });
        // Veio do checklist "Comece com o pé direito"? Volta pra Home.
        if (returnHomeIfRequested(() => setActiveModule("home"))) return;
        if (newId) {
            setActiveProId(newId);
            setView("detail");
        }
    };

    const openCollaborator = (id) => {
        setActiveProId(id);
        setView("detail");
    };

    const backToHub = () => {
        setView("hub");
        setActiveProId(null);
    };

    return (
        <div data-testid="collaborators-module" className="space-y-5">
            {view === "hub" && (
                <CollaboratorsHub
                    professionals={professionals}
                    onOpen={openCollaborator}
                    onAddNew={() => setNewProOpen(true)}
                />
            )}

            {view === "detail" && pro && (
                <CollaboratorDetail pro={pro} onBack={backToHub} provisioned={provisioned} />
            )}

            <NewCollaboratorModal
                open={newProOpen}
                onClose={() => setNewProOpen(false)}
                onCreate={handleNewProCreate}
            />
        </div>
    );
};

// ─── Avatar: sem foto → inicial do nome num círculo colorido (nunca foto aleatória) ─
const AVATAR_COLORS = ["#d4af37", "#c9983a", "#a3d8b1", "#00e5ff", "#8b5cf6", "#f59e0b", "#38bdf8", "#f472b6"];
const avatarColorFor = (name) => AVATAR_COLORS[(name || "?").trim().toUpperCase().charCodeAt(0) % AVATAR_COLORS.length];
const avatarInitial = (name) => (name || "").trim().charAt(0).toUpperCase();

const validateAvatarFile = (file) => {
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
        toast.error("Envie uma imagem PNG, JPG ou WEBP.");
        return false;
    }
    if (file.size > 2 * 1024 * 1024) {
        toast.error("Arquivo maior que 2 MB.");
        return false;
    }
    return true;
};

/**
 * AvatarUploadField — foto (ou fallback com inicial) + botão de câmera. Ao
 * escolher um arquivo, abre o ImageCropperModal (drag/zoom estilo Instagram)
 * em vez de subir a foto crua; só chama onChange com o JPEG 400x400 já
 * recortado, ao confirmar.
 */
const AvatarUploadField = ({ avatar, name, onChange, testId = "avatar-upload" }) => {
    const [pendingFile, setPendingFile] = useState(null);
    const inputRef = useRef(null);

    const handleFile = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ""; // permite selecionar o mesmo arquivo de novo
        if (!file || !validateAvatarFile(file)) return;
        setPendingFile(file);
    };

    return (
        <div className="flex items-center gap-4">
            <div className="relative h-20 w-20 shrink-0">
                {avatar ? (
                    <img data-testid={`${testId}-preview`} src={avatar} alt="" className="h-20 w-20 rounded-2xl object-cover ring-2 ring-amber-500/40" />
                ) : (
                    <div
                        data-testid={`${testId}-fallback`}
                        className="h-20 w-20 rounded-2xl ring-2 ring-amber-500/40 flex items-center justify-center text-white text-2xl font-bold"
                        style={{ background: avatarColorFor(name) }}
                    >
                        {avatarInitial(name) || <i className="fa-solid fa-user text-xl"></i>}
                    </div>
                )}
                <label
                    htmlFor={`${testId}-input`}
                    data-testid={`${testId}-trigger`}
                    title="Trocar foto"
                    className="absolute -bottom-1 -right-1 h-11 w-11 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-950 flex items-center justify-center cursor-pointer border-2 border-slate-950 shadow-lg transition-all"
                >
                    <i className="fa-solid fa-camera text-sm"></i>
                    <input
                        ref={inputRef}
                        id={`${testId}-input`}
                        data-testid={`${testId}-file`}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={handleFile}
                    />
                </label>
            </div>
            <div className="text-[11px] text-slate-500 leading-relaxed">
                PNG, JPG ou WEBP · até 2 MB.
                {avatar && (
                    <button
                        type="button"
                        data-testid={`${testId}-remove`}
                        onClick={() => onChange("")}
                        className="block mt-1.5 text-red-300/80 hover:text-red-300 uppercase tracking-wider text-[10px] font-bold"
                    >
                        <i className="fa-solid fa-trash mr-1"></i>Remover foto
                    </button>
                )}
            </div>

            {pendingFile && (
                <ImageCropperModal
                    file={pendingFile}
                    onConfirm={(jpeg) => { onChange(jpeg); setPendingFile(null); }}
                    onCancel={() => setPendingFile(null)}
                    onChangePhoto={() => { setPendingFile(null); inputRef.current?.click(); }}
                />
            )}
        </div>
    );
};

// ─── Hub: grid de cards de colaboradores + card "+ Adicionar" ────────────
const HUB_FILTERS = [
    { id: "all", label: "Todos", icon: "fa-users" },
    { id: "active", label: "Ativos", icon: "fa-circle-check" },
    { id: "byRole", label: "Por Cargo", icon: "fa-layer-group" },
    { id: "topRevenue", label: "Top Faturamento do Mês", icon: "fa-trophy" },
];

// Monthly revenue per pro from `transactions[].total` (current calendar month).
const computeMonthlyRevenue = (pro) => {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    return (pro.transactions || [])
        .filter((t) => (t.date || 0) >= monthStart)
        .reduce((s, t) => s + (t.total || 0), 0);
};

const RANK_BADGES = {
    0: { icon: "🥇", label: "Top 1 do mês", color: "#f59e0b" },
    1: { icon: "🥈", label: "Top 2 do mês", color: "#94a3b8" },
    2: { icon: "🥉", label: "Top 3 do mês", color: "#d97706" },
};

const CollaboratorsHub = ({ professionals, onOpen, onAddNew }) => {
    const [filterId, setFilterId] = useState("all");

    // Enrich with monthly revenue for sorting/badges
    const enriched = useMemo(
        () =>
            professionals.map((p) => ({ ...p, _monthlyRevenue: computeMonthlyRevenue(p) })),
        [professionals]
    );

    // Filter/sort based on selected mode
    const displayed = useMemo(() => {
        if (filterId === "active") {
            return enriched.filter((p) => p.active !== false);
        }
        if (filterId === "topRevenue") {
            return [...enriched].sort((a, b) => b._monthlyRevenue - a._monthlyRevenue);
        }
        // 'byRole' and 'all' share the same list; grouping happens later for 'byRole'
        return enriched;
    }, [enriched, filterId]);

    // Compute rank for top revenue mode (only pros with revenue > 0 get a medal)
    const rankMap = useMemo(() => {
        if (filterId !== "topRevenue") return {};
        const ranked = [...enriched]
            .filter((p) => p._monthlyRevenue > 0)
            .sort((a, b) => b._monthlyRevenue - a._monthlyRevenue);
        const map = {};
        ranked.slice(0, 3).forEach((p, idx) => { map[p.id] = idx; });
        return map;
    }, [enriched, filterId]);

    // Group by role when needed
    const grouped = useMemo(() => {
        if (filterId !== "byRole") return null;
        const map = {};
        displayed.forEach((p) => {
            const role = (p.role || "Sem cargo").trim();
            if (!map[role]) map[role] = [];
            map[role].push(p);
        });
        // Sort roles alphabetically, and pros inside each group by name
        return Object.keys(map)
            .sort((a, b) => a.localeCompare(b, "pt-BR"))
            .map((role) => ({
                role,
                items: map[role].sort((a, b) => (a.name || "").localeCompare(b.name || "", "pt-BR")),
            }));
    }, [displayed, filterId]);

    const totalActive = enriched.filter((p) => p.active !== false).length;

    return (
        <>
            <header className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-amber-300/60">Equipe</div>
                    <h1 className="salon-heading text-3xl text-amber-100 mt-1">Colaboradores</h1>
                    <p className="text-sm text-amber-200/50 mt-1">
                        {totalActive}/{enriched.length} ativo{totalActive === 1 ? "" : "s"} · clique num card para abrir a ficha completa.
                    </p>
                </div>
            </header>

            {/* Filter/sort bar */}
            <div
                data-testid="collab-hub-filters"
                className="flex flex-wrap gap-1 p-1 rounded-xl bg-black/40 border border-amber-500/10 max-w-fit"
            >
                {HUB_FILTERS.map((f) => (
                    <button
                        key={f.id}
                        data-testid={`collab-hub-filter-${f.id}`}
                        onClick={() => setFilterId(f.id)}
                        className={`min-h-[36px] px-3 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all inline-flex items-center gap-2 ${
                            filterId === f.id
                                ? "bg-amber-500/20 text-amber-100 border border-amber-400/60"
                                : "text-amber-200/60 hover:text-amber-100 border border-transparent"
                        }`}
                    >
                        <i className={`fa-solid ${f.icon} text-[10px]`}></i>
                        {f.label}
                    </button>
                ))}
            </div>

            {filterId === "byRole" && grouped ? (
                <div data-testid="collab-hub-groups" className="space-y-6">
                    {grouped.map((g) => (
                        <div key={g.role} data-testid={`collab-hub-role-${g.role}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <span className="salon-heading text-lg text-amber-200">{g.role}</span>
                                <span className="text-[10px] uppercase tracking-wider text-amber-300/50 font-bold">
                                    {g.items.length} pessoa{g.items.length === 1 ? "" : "s"}
                                </span>
                                <div className="flex-1 h-px bg-gradient-to-r from-amber-500/30 to-transparent"></div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr">
                                {g.items.map((p) => (
                                    <CollaboratorCard
                                        key={p.id}
                                        pro={p}
                                        onClick={() => onOpen(p.id)}
                                        rank={rankMap[p.id]}
                                        showRevenue={false}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                    <AddCollaboratorCard onClick={onAddNew} />
                </div>
            ) : (
                <div
                    data-testid="collab-hub-cards"
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 auto-rows-fr"
                >
                    {displayed.map((p) => (
                        <CollaboratorCard
                            key={p.id}
                            pro={p}
                            onClick={() => onOpen(p.id)}
                            rank={rankMap[p.id]}
                            showRevenue={filterId === "topRevenue"}
                        />
                    ))}
                    <AddCollaboratorCard onClick={onAddNew} />
                </div>
            )}
        </>
    );
};

const CollaboratorCard = ({ pro, onClick, rank, showRevenue }) => {
    const isInactive = pro.active === false;
    const badge = rank != null ? RANK_BADGES[rank] : null;
    return (
        <button
            data-testid={`collab-card-${pro.id}`}
            onClick={onClick}
            className={`group relative rounded-2xl border overflow-hidden text-left transition-all duration-300 min-h-[160px] p-4 ${
                isInactive
                    ? "border-white/8 bg-black/30 opacity-60"
                    : badge
                        ? "border-amber-400/50 bg-gradient-to-br from-amber-500/10 via-black/50 to-black/60 hover:border-amber-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.55)]"
                        : "border-amber-500/20 bg-gradient-to-br from-black/60 via-black/50 to-amber-500/[0.03] hover:border-amber-400/60 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(212,175,55,0.5)]"
            }`}
        >
            {/* Rank badge — top-right (Top Faturamento mode) */}
            {badge && (
                <span
                    data-testid={`collab-card-rank-${pro.id}`}
                    className="absolute top-3 right-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] uppercase tracking-wider font-bold border shadow-lg backdrop-blur"
                    style={{ background: `${badge.color}25`, color: badge.color, borderColor: `${badge.color}55` }}
                    title={badge.label}
                >
                    <span className="text-sm leading-none">{badge.icon}</span>
                    <span className="hidden sm:inline">{badge.label}</span>
                </span>
            )}

            {/* Photo top-left */}
            <div className="flex items-start gap-3">
                {pro.avatar ? (
                    <img
                        src={pro.avatar}
                        alt=""
                        className={`h-16 w-16 rounded-xl object-cover shrink-0 ring-2 transition-all duration-300 ${
                            isInactive
                                ? "ring-white/10 grayscale"
                                : badge
                                    ? "ring-amber-300 group-hover:ring-amber-200 group-hover:scale-[1.05]"
                                    : "ring-amber-500/40 group-hover:ring-amber-400 group-hover:scale-[1.05]"
                        }`}
                    />
                ) : (
                    <div
                        className={`h-16 w-16 rounded-xl shrink-0 ring-2 flex items-center justify-center text-white text-xl font-bold transition-all duration-300 ${
                            isInactive
                                ? "ring-white/10 grayscale"
                                : badge
                                    ? "ring-amber-300 group-hover:ring-amber-200 group-hover:scale-[1.05]"
                                    : "ring-amber-500/40 group-hover:ring-amber-400 group-hover:scale-[1.05]"
                        }`}
                        style={{ background: avatarColorFor(pro.name) }}
                    >
                        {avatarInitial(pro.name) || <i className="fa-solid fa-user text-base"></i>}
                    </div>
                )}
                {isInactive && (
                    <span className="ml-auto px-2 py-0.5 rounded-md text-[9px] uppercase tracking-wider font-bold bg-slate-800/60 text-slate-400 border border-white/5">
                        Inativo
                    </span>
                )}
            </div>

            {/* Name + role */}
            <div className="mt-4">
                <h3 className="salon-heading text-lg text-amber-100 leading-tight truncate">
                    {pro.name}
                </h3>
                <p className="text-[11px] uppercase tracking-[0.15em] text-amber-300/70 font-bold mt-1 truncate">
                    {pro.role || "Colaborador"}
                </p>
            </div>

            {/* Bottom stats */}
            <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between text-[10px] text-amber-200/50">
                {showRevenue ? (
                    <span className="font-mono text-amber-200/80">
                        <i className="fa-solid fa-sack-dollar mr-1 opacity-70"></i>
                        R$ {Number(pro._monthlyRevenue || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}
                    </span>
                ) : (
                    <span className="font-mono">
                        <i className="fa-solid fa-percent mr-1 opacity-60"></i>
                        {pro.commissionPct != null ? `${pro.commissionPct}%` : "—"}
                    </span>
                )}
                <i className="fa-solid fa-arrow-up-right text-amber-300/60 group-hover:text-amber-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"></i>
            </div>

            {/* Hover accent */}
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-amber-400/60 to-transparent scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
        </button>
    );
};

const AddCollaboratorCard = ({ onClick }) => (
    <button
        data-testid="collab-card-add"
        onClick={onClick}
        className="group relative rounded-2xl border-2 border-dashed border-amber-500/25 hover:border-amber-400/70 bg-black/20 hover:bg-amber-500/5 transition-all duration-300 min-h-[160px] flex flex-col items-center justify-center gap-3 hover:-translate-y-1"
    >
        <div className="h-16 w-16 rounded-2xl border border-amber-500/35 bg-amber-500/8 group-hover:bg-amber-500/20 group-hover:border-amber-400/70 flex items-center justify-center text-amber-300 text-2xl transition-all duration-300 group-hover:scale-110 group-hover:rotate-90">
            <i className="fa-solid fa-plus"></i>
        </div>
        <div className="text-center">
            <div className="salon-heading text-base text-amber-100">Adicionar</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                Novo Colaborador
            </div>
        </div>
    </button>
);

// ─── Detalhe: página única com Cadastro + Financeiro + Agenda ────────────
const CollaboratorDetail = ({ pro, onBack, provisioned }) => (
    <>
        <header
            data-testid="collab-detail-header"
            className="rounded-xl px-4 py-2.5 flex items-center gap-3 border border-amber-500/15 bg-black/40"
        >
            <button
                data-testid="collab-back-to-hub"
                onClick={onBack}
                className="min-h-[40px] px-3 rounded-lg border border-amber-500/25 hover:border-amber-400/60 hover:bg-amber-500/10 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all flex items-center gap-2"
                title="Voltar aos cards"
            >
                <i className="fa-solid fa-arrow-left"></i>
                <span className="hidden sm:inline">Voltar</span>
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-3">
                <div className="text-right hidden sm:block">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-amber-300/60 font-bold">Colaborador</div>
                    <div className="text-sm text-amber-100 font-bold">{pro.name}</div>
                </div>
                {pro.avatar ? (
                    <img
                        src={pro.avatar}
                        alt=""
                        className="h-11 w-11 rounded-xl object-cover ring-2 ring-amber-500/40"
                    />
                ) : (
                    <div
                        className="h-11 w-11 rounded-xl ring-2 ring-amber-500/40 flex items-center justify-center text-white text-sm font-bold"
                        style={{ background: avatarColorFor(pro.name) }}
                    >
                        {avatarInitial(pro.name) || <i className="fa-solid fa-user text-xs"></i>}
                    </div>
                )}
            </div>
        </header>

        {/* Section anchors */}
        <SectionHeader index="01" label="Cadastro do Colaborador" icon="fa-id-badge" />
        <RegistryView pro={pro} provisioned={provisioned} />

        <SectionHeader index="02" label="Financeiro do Colaborador" icon="fa-circle-dollar-to-slot" />
        <FinancialView pro={pro} />

        <SectionHeader index="03" label="Agenda Semanal" icon="fa-calendar-week" />
        <AgendaView pro={pro} />
    </>
);

const SectionHeader = ({ index, label, icon }) => (
    <div className="flex items-center gap-3 mt-8 mb-3 pb-2 border-b border-amber-500/15">
        <div className="h-9 w-9 rounded-lg border border-amber-500/30 bg-amber-500/10 flex items-center justify-center text-amber-300">
            <i className={`fa-solid ${icon}`}></i>
        </div>
        <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300/60">Seção {index}</div>
            <h2 className="salon-heading text-xl text-amber-100">{label}</h2>
        </div>
    </div>
);

// ─── Senha de acesso ao app (Seção 1) ─────────────────────────────────────
const normEmail = (v) => String(v || "").trim().toLowerCase();
const isBackendId = (id) => /^[0-9a-f]{8}-[0-9a-f]{4}-/.test(String(id || ""));
const fmtDateShort = (v) => {
    if (!v) return "—";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
};

/** Caixa em destaque com a senha em texto — aparece SÓ no momento da geração. */
const PasswordRevealBox = ({ password }) => {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        try {
            navigator.clipboard.writeText(password);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };
    return (
        <div data-testid="registry-password-reveal" className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3.5 space-y-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 font-bold flex items-center gap-1.5">
                <i className="fa-solid fa-circle-check"></i>Senha de acesso gerada
            </div>
            <div className="flex items-center gap-2">
                <code
                    data-testid="registry-password-value"
                    className="flex-1 min-w-0 truncate rounded-lg bg-black/50 border border-emerald-500/30 px-3 py-2 font-mono text-base text-emerald-100 tracking-wider"
                >
                    {password}
                </code>
                <button
                    type="button"
                    data-testid="registry-password-copy"
                    onClick={copy}
                    className="shrink-0 min-h-[40px] px-3 rounded-lg border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/15 text-xs font-bold uppercase tracking-wider transition-all"
                >
                    <i className={`fa-solid ${copied ? "fa-check" : "fa-copy"} mr-1`}></i>{copied ? "Copiado" : "Copiar"}
                </button>
            </div>
            <p className="text-[11px] text-emerald-200/70 leading-snug">
                Essa senha só aparece agora — já foi enviada por WhatsApp também. Anote se precisar.
                O colaborador troca a senha no primeiro login.
            </p>
        </div>
    );
};

// ─── Cadastro do Colaborador + Gráfico ─────────────────────────────────────
const CHART_PERIODS = [
    { id: "weekly", label: "Semanal", days: 7, buckets: 7, unit: "day" },
    { id: "biweekly", label: "Quinzenal", days: 15, buckets: 15, unit: "day" },
    { id: "monthly", label: "Mensal", days: 30, buckets: 4, unit: "week" },
    { id: "quarterly", label: "Trimestral", days: 90, buckets: 12, unit: "week" },
    { id: "semester", label: "Semestral", days: 180, buckets: 6, unit: "month" },
    { id: "annual", label: "Anual", days: 365, buckets: 12, unit: "month" },
];

const RegistryView = ({ pro, provisioned }) => {
    const { upsertProfessional, toggleProfessional, deleteProfessional, syncTenantData } = useSalon();
    const [draft, setDraft] = useState(null);
    const [periodId, setPeriodId] = useState("monthly");
    const [regenResult, setRegenResult] = useState(null); // senha nova recém-gerada (só nesta sessão da tela)
    const [regenerating, setRegenerating] = useState(false);

    const editing = draft || {
        id: pro?.id,
        name: pro?.name || "",
        role: pro?.role || "",
        phone: pro?.phone || "",
        email: pro?.email || "",
        pixKey: pro?.pixKey || "",
        avatar: pro?.avatar || "",
        fixedSalary: pro?.fixedSalary || { active: false, value: 0 },
        commission: pro?.commission || { active: true, rate: Math.round((pro?.commissionRate || 0.4) * 100) },
        active: pro?.active !== false,
    };
    const hasAppAccess = !!(pro?.email && pro?.access_provisioned_at);
    const canProvision = !!(normEmail(pro?.email) && (pro?.phone || "").trim());

    // Senha em destaque: da regeneração local OU do create recém-feito (casado por e-mail).
    const justCreatedPw =
        provisioned && normEmail(provisioned.email) && normEmail(provisioned.email) === normEmail(pro?.email)
            ? provisioned.password
            : null;
    const revealPassword = regenResult || justCreatedPw;
    const backendId = isBackendId(pro?.id) ? pro.id : null;

    const handleRegenerate = async () => {
        if (!backendId) {
            toast.info("Salve o cadastro primeiro para habilitar a geração de senha.");
            return;
        }
        setRegenerating(true);
        try {
            const res = await api.post(`/tenant/professionals/${backendId}/regenerate-password`, {});
            setRegenResult(res?.temp_password || null);
            if (res?.access_sent) {
                toast.success("Nova senha enviada por WhatsApp", { description: pro?.phone || "" });
            } else {
                toast.warning("Nova senha gerada — mas o WhatsApp não confirmou o envio", {
                    description: "A senha está na tela. Verifique a conexão do WhatsApp do salão.",
                });
            }
            syncTenantData?.();
        } catch (e) {
            toast.error("Não foi possível gerar a nova senha", { description: e?.message });
        } finally {
            setRegenerating(false);
        }
    };

    const setField = (k, v) => setDraft({ ...editing, [k]: v });
    const setFixed = (patch) =>
        setDraft({ ...editing, fixedSalary: { ...editing.fixedSalary, ...patch } });
    const setCom = (patch) =>
        setDraft({ ...editing, commission: { ...editing.commission, ...patch } });

    const handleSave = () => {
        if (!editing.name.trim()) {
            toast.error("Informe o nome do colaborador.");
            return;
        }
        if (!editing.fixedSalary.active && !editing.commission.active) {
            toast.error("Ative ao menos um modo de ganho.");
            return;
        }
        if (!draft) {
            toast.info("Nenhuma alteração para salvar.");
            return;
        }
        upsertProfessional({
            ...editing,
            commissionRate: (editing.commission?.rate ?? 0) / 100,
        }, {
            onSaved: (res) => {
                if (res?.temp_password) {
                    setRegenResult(res.temp_password);
                    syncTenantData?.();
                }
                if (res?.access_sent) {
                    toast.success("Acesso ao app enviado", {
                        description: `Senha temporária enviada por WhatsApp para ${editing.phone}.`,
                    });
                }
            },
        });
        toast.success("Cadastro atualizado");
        setDraft(null);
    };

    const period = CHART_PERIODS.find((p) => p.id === periodId);

    // Aggregate transactions into buckets for the chart
    const chartData = useMemo(() => {
        if (!pro || !period) return { buckets: [], maxValue: 0 };
        const now = Date.now();
        const startTime = now - period.days * 86400000;
        const txns = (pro.transactions || []).filter((t) => t.date >= startTime);

        const bucketSize = period.days / period.buckets;
        const buckets = Array.from({ length: period.buckets }, (_, i) => {
            const bucketStart = startTime + i * bucketSize * 86400000;
            const bucketEnd = bucketStart + bucketSize * 86400000;
            const items = txns.filter((t) => t.date >= bucketStart && t.date < bucketEnd);
            const total = items.reduce((s, t) => s + t.total, 0);
            const share = items.reduce((s, t) => s + t.share, 0);
            const label = formatBucketLabel(new Date(bucketStart), period.unit);
            return { label, total, share, count: items.length };
        });
        const maxValue = Math.max(1, ...buckets.map((b) => b.total));
        return { buckets, maxValue };
    }, [pro, period]);

    if (!pro) return null;

    return (
        <div data-testid="collab-registry-view" className="grid lg:grid-cols-[440px_1fr] gap-4">
            {/* Left: Cadastro do Colaborador */}
            <div className="gold-panel p-5 lg:p-6" data-testid="registry-form-panel">
                <header className="gold-text text-center text-base uppercase pb-3 gold-border-bottom">
                    Cadastro
                </header>
                <div className="space-y-4 mt-4">
                    <AvatarUploadField
                        avatar={editing.avatar}
                        name={editing.name}
                        onChange={(v) => setField("avatar", v)}
                        testId="registry-avatar"
                    />
                    <GoldField label="Nome Completo" testId="registry-name">
                        <input
                            value={editing.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="Nome completo do colaborador"
                            className={goldInputCls}
                        />
                    </GoldField>
                    <GoldField label="Cargo" testId="registry-role">
                        <input
                            value={editing.role}
                            onChange={(e) => setField("role", e.target.value)}
                            placeholder="Barbeiro, Cabeleireira, Esteticista…"
                            className={goldInputCls}
                        />
                    </GoldField>
                    <div className="grid grid-cols-2 gap-3">
                        <GoldField label="Telefone" testId="registry-phone">
                            <input
                                value={editing.phone}
                                onChange={(e) => setField("phone", maskPhone(e.target.value))}
                                placeholder="(00) 00000-0000"
                                className={`${goldInputCls} font-mono`}
                            />
                        </GoldField>
                        <GoldField label="Chave PIX" testId="registry-pix">
                            <input
                                value={editing.pixKey}
                                onChange={(e) => setField("pixKey", e.target.value)}
                                placeholder="CPF, e-mail, telefone…"
                                className={goldInputCls}
                            />
                        </GoldField>
                    </div>
                    <GoldField label="E-mail (acesso pessoal ao app)" testId="registry-email">
                        <input
                            type="email"
                            value={editing.email}
                            onChange={(e) => setField("email", e.target.value)}
                            placeholder="colaborador@email.com"
                            className={goldInputCls}
                            disabled={hasAppAccess}
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                            {hasAppAccess
                                ? "Acesso ao app já ativo. O colaborador entra com este e-mail e a própria senha."
                                : "Preencha e-mail + telefone e salve: o colaborador recebe uma senha temporária por WhatsApp para acessar a própria agenda e comissão no app."}
                        </p>
                    </GoldField>

                    {/* Senha de acesso ao app */}
                    <div data-testid="registry-app-password" className="rounded-xl border border-amber-500/15 bg-black/30 p-3.5 space-y-2.5">
                        <div className="text-xs text-amber-200 font-semibold flex items-center gap-1.5">
                            <i className="fa-solid fa-key text-amber-300 text-[11px]"></i>Senha de acesso ao app
                        </div>

                        {revealPassword ? (
                            <PasswordRevealBox password={revealPassword} />
                        ) : hasAppAccess ? (
                            <div className="text-[12px] text-slate-400" data-testid="registry-app-password-status">
                                Senha definida em <b className="text-slate-200">{fmtDateShort(pro?.access_provisioned_at)}</b>
                                {" · "}
                                {pro?.must_change_password
                                    ? <span className="text-amber-300/80">aguardando primeiro acesso</span>
                                    : <span className="text-emerald-300/80">primeiro acesso já feito</span>}
                                <div className="text-[11px] text-slate-600 mt-0.5">A senha não fica guardada — só o status aparece aqui.</div>
                            </div>
                        ) : (
                            <div className="text-[12px] text-slate-500" data-testid="registry-app-password-status">
                                {canProvision
                                    ? "Salve o cadastro para gerar e enviar a senha de acesso."
                                    : "Preencha e-mail + telefone para habilitar o acesso ao app."}
                            </div>
                        )}

                        {(hasAppAccess || canProvision) && (
                            <div className="pt-0.5">
                                <button
                                    type="button"
                                    data-testid="registry-regenerate-password"
                                    onClick={handleRegenerate}
                                    disabled={regenerating || !backendId}
                                    className="min-h-[40px] px-3.5 rounded-lg border border-amber-500/30 text-amber-200 hover:bg-amber-500/10 text-[11px] font-bold uppercase tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <i className={`fa-solid ${regenerating ? "fa-spinner fa-spin" : "fa-rotate"} mr-1.5`}></i>
                                    Gerar nova senha e reenviar por WhatsApp
                                </button>
                                <p className="text-[11px] text-slate-600 mt-1">
                                    Isso gera uma senha <b className="text-slate-400">nova</b> — a anterior deixa de funcionar. Não reenvia a senha antiga.
                                    {!backendId && " (salve o cadastro primeiro)"}
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Ganhos */}
                    <div className="rounded-xl border border-amber-500/22 bg-black/40 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-money-bill-wave text-amber-300"></i>
                                <span className="text-sm text-amber-200 font-semibold">Salário Fixo</span>
                            </div>
                            <Toggle
                                checked={editing.fixedSalary.active}
                                onChange={() => setFixed({ active: !editing.fixedSalary.active })}
                                testId="registry-fixed-toggle"
                            />
                        </div>
                        <input
                            data-testid="registry-fixed-value"
                            type="number"
                            min={0}
                            value={editing.fixedSalary.value}
                            onChange={(e) => setFixed({ value: parseFloat(e.target.value) || 0 })}
                            disabled={!editing.fixedSalary.active}
                            placeholder="R$ 0,00"
                            className={`${goldInputCls} font-mono disabled:opacity-40`}
                        />
                    </div>

                    <div className="rounded-xl border border-amber-500/22 bg-black/40 p-4 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-percent text-amber-300"></i>
                                <span className="text-sm text-amber-200 font-semibold">Comissão</span>
                            </div>
                            <Toggle
                                checked={editing.commission.active}
                                onChange={() => setCom({ active: !editing.commission.active })}
                                testId="registry-com-toggle"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <input
                                data-testid="registry-com-rate"
                                type="number"
                                min={0}
                                max={100}
                                value={editing.commission.rate}
                                onChange={(e) =>
                                    setCom({ rate: Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)) })
                                }
                                disabled={!editing.commission.active}
                                className={`${goldInputCls} font-mono disabled:opacity-40 flex-1`}
                            />
                            <span className="text-amber-300 font-mono text-lg">%</span>
                        </div>
                    </div>

                    <div className="flex items-center justify-between gap-2 pt-2">
                        <div className="flex items-center gap-2">
                            <button
                                data-testid="registry-toggle-active"
                                onClick={() => {
                                    toggleProfessional(pro.id);
                                    setDraft({ ...editing, active: !(editing.active !== false) });
                                }}
                                className="min-h-[44px] px-3 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 text-[10px] uppercase tracking-wider transition-all"
                                title={editing.active === false ? "Ativar" : "Suspender"}
                            >
                                <i className={`fa-solid ${editing.active === false ? "fa-play" : "fa-pause"}`}></i>
                            </button>
                            <button
                                data-testid="registry-delete-btn"
                                onClick={() => {
                                    if (window.confirm(`Excluir ${pro.name}?`)) {
                                        deleteProfessional(pro.id);
                                        toast.success("Colaborador removido");
                                    }
                                }}
                                className="min-h-[44px] h-11 w-11 rounded-xl border border-red-500/25 hover:border-red-500/60 text-red-400 transition-all"
                            >
                                <i className="fa-solid fa-trash text-xs"></i>
                            </button>
                        </div>
                        <button
                            data-testid="registry-save-btn"
                            onClick={handleSave}
                            className="gold-pill gold-pill-active min-h-[48px] px-6 rounded-xl text-xs font-bold uppercase tracking-[0.15em]"
                        >
                            <i className="fa-solid fa-floppy-disk mr-2"></i>Salvar
                        </button>
                    </div>
                </div>
            </div>

            {/* Right: Gráfico financeiro */}
            <div className="gold-panel p-5 lg:p-6" data-testid="registry-chart-panel">
                <header className="flex items-center justify-between pb-3 gold-border-bottom">
                    <h3 className="gold-text text-base uppercase">Histórico Financeiro</h3>
                    <div className="text-[11px] uppercase tracking-wider text-amber-200/70">
                        {chartData.buckets.reduce((s, b) => s + b.count, 0)} atendimentos no período
                    </div>
                </header>

                {/* Period selector */}
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-4">
                    {CHART_PERIODS.map((p) => {
                        const active = periodId === p.id;
                        return (
                            <button
                                key={p.id}
                                data-testid={`chart-period-${p.id}`}
                                onClick={() => setPeriodId(p.id)}
                                className={`gold-pill ${active ? "gold-pill-active" : ""} min-h-[44px] rounded-xl px-2 text-[10px] font-bold uppercase tracking-wider`}
                            >
                                {p.label}
                            </button>
                        );
                    })}
                </div>

                {/* Chart */}
                <div className="mt-6">
                    <ChartLegend />
                    <BarChart data={chartData} />
                    {/* Summary footer */}
                    <ChartSummary data={chartData} pro={pro} period={period} />
                </div>
            </div>
        </div>
    );
};

const ChartLegend = () => (
    <div className="flex items-center gap-4 text-[11px] text-amber-200/70 mb-3 px-1">
        <span className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-amber-500/30 border border-amber-500/40"></span>
            Faturamento bruto
        </span>
        <span className="flex items-center gap-2">
            <span className="inline-block h-3 w-3 rounded-sm bg-amber-400 border border-amber-300"></span>
            Comissão do colaborador
        </span>
    </div>
);

const BarChart = ({ data }) => {
    const { buckets, maxValue } = data;
    return (
        <div data-testid="financial-chart" className="relative h-72 rounded-xl border border-amber-500/15 bg-black/40 p-4 overflow-hidden">
            {/* horizontal grid lines */}
            {[1, 0.75, 0.5, 0.25, 0].map((p) => (
                <div
                    key={p}
                    className="absolute left-4 right-4 border-t border-amber-500/8 flex items-center"
                    style={{ top: `calc(${(1 - p) * 100}% + 16px - 1px)` }}
                >
                    <span className="text-[9px] font-mono text-amber-300/50 -mt-2 -ml-1 px-1 bg-black/60">
                        R$ {Math.round(maxValue * p)}
                    </span>
                </div>
            ))}

            <div className="absolute inset-4 flex items-end justify-between gap-1">
                {buckets.map((b, i) => {
                    const totalH = (b.total / maxValue) * 100;
                    const shareH = (b.share / maxValue) * 100;
                    return (
                        <div
                            key={i}
                            className="flex-1 flex flex-col items-center justify-end gap-1 h-full group"
                            data-testid={`chart-bar-${i}`}
                            title={`${b.label} · R$ ${b.total.toFixed(0)} (comissão R$ ${b.share.toFixed(2)})`}
                        >
                            <div className="relative w-full max-w-[36px] flex-1 flex items-end">
                                {/* Total (back) */}
                                <div
                                    className="absolute bottom-0 left-0 right-0 rounded-t bg-amber-500/25 border-t border-amber-500/40 transition-all group-hover:bg-amber-500/40"
                                    style={{
                                        height: `${totalH}%`,
                                        animation: `chartGrow 0.6s ease-out ${i * 40}ms both`,
                                    }}
                                ></div>
                                {/* Commission (front) */}
                                <div
                                    className="absolute bottom-0 left-1/4 right-1/4 rounded-t bg-amber-400 transition-all"
                                    style={{
                                        height: `${shareH}%`,
                                        animation: `chartGrow 0.6s ease-out ${i * 40 + 80}ms both`,
                                        boxShadow: "0 -2px 12px rgba(212,175,55,0.4)",
                                    }}
                                ></div>
                            </div>
                            <span className="text-[9px] font-mono text-amber-200/60 truncate max-w-full">
                                {b.label}
                            </span>
                        </div>
                    );
                })}
            </div>

            <style>{`@keyframes chartGrow { from { height: 0%; opacity: 0; } to { opacity: 1; } }`}</style>
        </div>
    );
};

const ChartSummary = ({ data, pro, period }) => {
    const totalRevenue = data.buckets.reduce((s, b) => s + b.total, 0);
    const totalCommission = data.buckets.reduce((s, b) => s + b.share, 0);
    const fixed = pro?.fixedSalary?.active ? (pro.fixedSalary.value / 30) * (period?.days || 0) : 0;
    const toReceive = totalCommission + fixed;
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
            <ChartKpi label="Faturamento" value={`R$ ${totalRevenue.toFixed(0)}`} />
            <ChartKpi label="Comissão" value={`R$ ${totalCommission.toFixed(2)}`} />
            <ChartKpi label="Fixo proporcional" value={`R$ ${fixed.toFixed(2)}`} />
            <ChartKpi label="A receber" value={`R$ ${toReceive.toFixed(2)}`} highlight />
        </div>
    );
};

const ChartKpi = ({ label, value, highlight }) => (
    <div
        className={`rounded-xl border bg-black/40 p-3 ${
            highlight ? "border-emerald-500/30" : "border-amber-500/18"
        }`}
    >
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60">{label}</div>
        <div
            className="salon-heading text-lg mt-1 font-mono"
            style={{ color: highlight ? "#34d399" : "#fde7a8" }}
        >
            {value}
        </div>
    </div>
);

const formatBucketLabel = (date, unit) => {
    if (unit === "day") {
        return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    }
    if (unit === "week") {
        return `S${Math.ceil(date.getDate() / 7)}/${String(date.getMonth() + 1).padStart(2, "0")}`;
    }
    return date.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
};

// ─── VIEW: Financeiro do Colaborador ──────────────────────────────────────
const FinancialView = ({ pro }) => {
    const [periodId, setPeriodId] = useState("monthly");
    const PERIODS_SIMPLE = {
        weekly: { days: 7, label: "Semanal" },
        biweekly: { days: 15, label: "Quinzenal" },
        monthly: { days: 30, label: "Mensal" },
    };

    const transactions = useMemo(() => {
        if (!pro) return [];
        const cutoff = Date.now() - PERIODS_SIMPLE[periodId].days * 86400000;
        return (pro.transactions || []).filter((t) => t.date >= cutoff).sort((a, b) => b.date - a.date);
    }, [pro, periodId]);

    const stats = useMemo(() => {
        const count = transactions.length;
        const revenue = transactions.reduce((s, t) => s + t.total, 0);
        const commission = transactions.reduce((s, t) => s + t.share, 0);
        const fixed = pro?.fixedSalary?.active ? pro.fixedSalary.value : 0;
        const fixedPortion = (fixed / 30) * PERIODS_SIMPLE[periodId].days;
        return { count, revenue, commission, fixedPortion, toReceive: commission + fixedPortion };
    }, [transactions, pro, periodId]);

    if (!pro) return null;

    return (
        <div data-testid="collab-financial-view" className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
                {Object.entries(PERIODS_SIMPLE).map(([id, p]) => {
                    const active = periodId === id;
                    return (
                        <button
                            key={id}
                            data-testid={`collab-period-${id}`}
                            onClick={() => setPeriodId(id)}
                            className={`gold-pill ${active ? "gold-pill-active" : ""} min-h-[44px] rounded-xl text-xs font-bold uppercase tracking-[0.15em]`}
                        >
                            {p.label}
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <GoldKpi label="Serviços realizados" value={stats.count} icon="fa-scissors" />
                <GoldKpi label="Faturamento bruto" value={`R$ ${stats.revenue.toFixed(0)}`} icon="fa-coins" />
                <GoldKpi label="Comissão acumulada" value={`R$ ${stats.commission.toFixed(2)}`} icon="fa-percent" />
                <GoldKpi label="Saldo a receber" value={`R$ ${stats.toReceive.toFixed(2)}`} icon="fa-hand-holding-dollar" highlight />
            </div>

            <div className="gold-panel overflow-hidden">
                <header className="px-5 py-4 gold-border-bottom flex items-center justify-between">
                    <h3 className="gold-text text-base uppercase">Histórico de atendimentos</h3>
                    <span className="text-[11px] uppercase tracking-wider text-amber-200/70">
                        PIX: <span className="font-mono text-amber-300">{pro.pixKey || "—"}</span>
                    </span>
                </header>
                <div className="overflow-x-auto max-h-[480px]">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70 bg-black/40 sticky top-0">
                                <th className="text-left py-3 px-4">Data</th>
                                <th className="text-left py-3 px-4">Serviço</th>
                                <th className="text-left py-3 px-4">Cliente</th>
                                <th className="text-right py-3 px-4">Total</th>
                                <th className="text-center py-3 px-4">%</th>
                                <th className="text-right py-3 px-4">Colaborador</th>
                            </tr>
                        </thead>
                        <tbody>
                            {transactions.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-14 text-center text-amber-200/40">
                                        <i className="fa-solid fa-folder-open text-2xl mb-2"></i>
                                        <p>Sem atendimentos no período</p>
                                    </td>
                                </tr>
                            )}
                            {transactions.map((t) => (
                                <tr key={t.id} className="border-t border-amber-500/10 hover:bg-amber-500/5 transition-colors">
                                    <td className="py-3 px-4 font-mono text-xs text-amber-200/80">
                                        {new Date(t.date).toLocaleDateString("pt-BR")}
                                    </td>
                                    <td className="py-3 px-4 text-amber-100">{t.service}</td>
                                    <td className="py-3 px-4 text-amber-200/60">{t.client}</td>
                                    <td className="py-3 px-4 text-right font-mono text-amber-100">
                                        R$ {t.total.toFixed(2)}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-mono text-[10px] font-bold">
                                            {t.rate}%
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-right font-mono text-emerald-300 font-bold">
                                        R$ {t.share.toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

// ─── VIEW: Agenda Semanal ─────────────────────────────────────────────────
const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const STATUS_STYLES = {
    agendado: { label: "Agendado", color: "amber", border: "border-amber-500/40", bg: "bg-amber-500/10", text: "text-amber-200" },
    "em-andamento": { label: "Em andamento", color: "blue", border: "border-sky-500/50", bg: "bg-sky-500/15", text: "text-sky-200" },
    concluido: { label: "Concluído", color: "emerald", border: "border-emerald-500/40", bg: "bg-emerald-500/10", text: "text-emerald-200" },
};
const MAX_RANGE_DAYS = 186; // ±~6 meses

const startOfWeek = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - d.getDay()); // Domingo
    return d;
};

const AgendaView = ({ pro }) => {
    const { appointments, upsertAppointment, deleteAppointment, services, clients, professionals } = useSalon();
    const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
    const [editing, setEditing] = useState(null); // { id?, date, time, client, service, status, ... }
    const [conflict, setConflict] = useState(null); // { appt, requestedStartMs, conflictAppt, nextFreeMs, otherPros: [{pro, startMs}] }
    const [conflictPreviewIdx, setConflictPreviewIdx] = useState(null); // day index where dropping would conflict

    const today = useMemo(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    }, []);
    const todayWeekStart = useMemo(() => startOfWeek(today), [today]);
    const weekOffsetDays = Math.round((weekStart.getTime() - todayWeekStart.getTime()) / 86400000);
    const canPrev = weekOffsetDays > -MAX_RANGE_DAYS;
    const canNext = weekOffsetDays < MAX_RANGE_DAYS;

    const days = useMemo(() => {
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(weekStart);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [weekStart]);

    const proAppointments = useMemo(
        () => appointments.filter((a) => a.professionalId === pro?.id && a.status !== "cancelado"),
        [appointments, pro]
    );

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

    const handleDragOver = (event) => {
        const { active, over } = event;
        if (!over) { setConflictPreviewIdx(null); return; }
        const dropTarget = over.data?.current;
        if (!dropTarget || dropTarget.kind !== "day") { setConflictPreviewIdx(null); return; }
        const appt = proAppointments.find((a) => a.id === active.id);
        if (!appt) { setConflictPreviewIdx(null); return; }
        const targetDay = new Date(dropTarget.dateMs);
        const orig = new Date(appt.date);
        targetDay.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
        if (targetDay.getTime() === appt.date) { setConflictPreviewIdx(null); return; }
        const duration = appt.duration_min || 30;
        const newStart = targetDay.getTime();
        const newEnd = newStart + duration * 60000;
        const has = proAppointments.some((a) => {
            if (a.id === appt.id) return false;
            const aStart = a.date;
            const aEnd = a.estimated_end || aStart + (a.duration_min || 30) * 60000;
            return aStart < newEnd && aEnd > newStart;
        });
        setConflictPreviewIdx(has ? dropTarget.dayIdx : null);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;
        const apptId = active.id;
        const dropTarget = over.data?.current;
        if (!dropTarget || dropTarget.kind !== "day") return;
        const appt = proAppointments.find((a) => a.id === apptId);
        if (!appt) return;
        const targetDay = new Date(dropTarget.dateMs);
        const orig = new Date(appt.date);
        // Keep hh:mm but move day
        targetDay.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
        if (targetDay.getTime() === appt.date) return; // no-op
        const duration = appt.duration_min || 30;
        const newStart = targetDay.getTime();
        const newEnd = newStart + duration * 60000;

        // Conflict detection within same pro
        const overlapping = proAppointments.filter((a) => {
            if (a.id === apptId) return false;
            const aStart = a.date;
            const aEnd = a.estimated_end || aStart + (a.duration_min || 30) * 60000;
            return aStart < newEnd && aEnd > newStart;
        });

        if (overlapping.length > 0) {
            // Compute smart resolution suggestions
            const dayStart = new Date(targetDay); dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(targetDay); dayEnd.setHours(23, 59, 59, 999);
            const dayApps = proAppointments
                .filter((a) => a.id !== apptId && a.date >= dayStart.getTime() && a.date <= dayEnd.getTime())
                .sort((x, y) => x.date - y.date);
            const nextFreeMs = findNextFreeSlot(dayApps, newStart, duration, dayEnd.getTime());

            // Find other available pros at the originally requested time
            const otherPros = professionals
                .filter((p) => p.id !== pro.id && p.active !== false)
                .map((p) => {
                    const conflicts = appointments.filter((a) => {
                        if (a.professionalId !== p.id || a.status === "cancelado") return false;
                        const aStart = a.date;
                        const aEnd = a.estimated_end || aStart + (a.duration_min || 30) * 60000;
                        return aStart < newEnd && aEnd > newStart;
                    });
                    return { pro: p, available: conflicts.length === 0 };
                })
                .filter((x) => x.available)
                .map((x) => x.pro);

            setConflict({
                appt,
                requestedStartMs: newStart,
                duration,
                conflictAppt: overlapping[0],
                nextFreeMs,
                otherPros,
            });
            return;
        }

        // No conflict — just move
        upsertAppointment({
            ...appt,
            date: newStart,
            estimated_end: newEnd,
            time: `${String(targetDay.getHours()).padStart(2, "0")}:${String(targetDay.getMinutes()).padStart(2, "0")}`,
        });
        toast.success("Agendamento movido", {
            description: `${appt.clientName} → ${targetDay.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })} ${String(targetDay.getHours()).padStart(2, "0")}:${String(targetDay.getMinutes()).padStart(2, "0")}`,
        });
    };

    const handleConflictPickSlot = () => {
        if (!conflict || !conflict.nextFreeMs) return;
        const d = new Date(conflict.nextFreeMs);
        upsertAppointment({
            ...conflict.appt,
            date: conflict.nextFreeMs,
            estimated_end: conflict.nextFreeMs + conflict.duration * 60000,
            time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        });
        toast.success("Reagendado", {
            description: `${conflict.appt.clientName} → ${d.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" })} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} com ${pro.name}`,
        });
        setConflict(null);
    };

    const handleConflictPickPro = (newPro) => {
        if (!conflict) return;
        const d = new Date(conflict.requestedStartMs);
        upsertAppointment({
            ...conflict.appt,
            date: conflict.requestedStartMs,
            estimated_end: conflict.requestedStartMs + conflict.duration * 60000,
            time: `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
            professionalId: newPro.id,
            professionalName: newPro.name,
        });
        toast.success("Reatribuído", {
            description: `${conflict.appt.clientName} → ${newPro.name} às ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`,
        });
        setConflict(null);
    };

    const appointmentsByDay = useMemo(() => {
        const map = Array.from({ length: 7 }, () => []);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 7);
        proAppointments.forEach((a) => {
            if (!a.date) return;
            const ad = new Date(a.date);
            if (ad >= weekStart && ad < weekEnd) {
                const idx = ad.getDay();
                // Normalize for display compatibility
                map[idx].push({ ...a, client: a.clientName, service: a.serviceName });
            }
        });
        map.forEach((list) => list.sort((a, b) => a.date - b.date));
        return map;
    }, [proAppointments, weekStart]);

    const goWeek = (delta) => {
        const next = new Date(weekStart);
        next.setDate(next.getDate() + delta * 7);
        const diff = Math.round((next.getTime() - todayWeekStart.getTime()) / 86400000);
        if (Math.abs(diff) > MAX_RANGE_DAYS) return;
        setWeekStart(next);
    };

    const openNew = (day) => {
        const d = new Date(day);
        d.setHours(10, 0, 0, 0);
        setEditing({ date: d.getTime(), time: "10:00", client: "", service: "", status: "agendado" });
    };

    const openEdit = (appt) => setEditing({ ...appt });

    const handleSave = () => {
        if (!editing.client.trim() || !editing.service.trim()) {
            toast.error("Informe cliente e serviço.");
            return;
        }
        const [hh, mm] = (editing.time || "10:00").split(":").map((x) => parseInt(x, 10) || 0);
        const d = new Date(editing.date);
        d.setHours(hh, mm, 0, 0);

        // Try to match service to catalog by name (fallback to existing duration or 30)
        const matchedService = services.find(
            (s) => s.name.toLowerCase() === editing.service.trim().toLowerCase()
        );
        const matchedClient = clients.find(
            (c) => c.name.toLowerCase() === editing.client.trim().toLowerCase()
        );
        const duration = editing.duration_min || matchedService?.duration || 30;

        upsertAppointment({
            ...editing,
            id: editing.id,
            date: d.getTime(),
            time: `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`,
            duration_min: duration,
            estimated_end: d.getTime() + duration * 60000,
            clientId: editing.clientId || matchedClient?.id || null,
            clientName: editing.client.trim(),
            professionalId: pro.id,
            professionalName: pro.name,
            serviceId: editing.serviceId || matchedService?.id || null,
            serviceName: editing.service.trim(),
            servicePrice: editing.servicePrice ?? matchedService?.price ?? 0,
            status: editing.status || "agendado",
            source: editing.source || "agenda",
        });
        toast.success(editing.id ? "Agendamento atualizado" : "Agendamento criado");
        setEditing(null);
    };

    const handleDelete = () => {
        if (!editing?.id) return setEditing(null);
        if (!window.confirm("Remover este agendamento?")) return;
        deleteAppointment(editing.id);
        toast.success("Agendamento removido");
        setEditing(null);
    };

    if (!pro) return null;

    const rangeLabel = `${days[0].toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} – ${days[6].toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })}`;

    return (
        <div data-testid="collab-agenda-view" className="space-y-4">
            {/* Header com navegação */}
            <div className="gold-panel p-4 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                    <button
                        data-testid="agenda-prev-week"
                        onClick={() => goWeek(-1)}
                        disabled={!canPrev}
                        className="min-h-[44px] h-11 w-11 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        title="Semana anterior"
                    >
                        <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    <button
                        data-testid="agenda-today"
                        onClick={() => setWeekStart(todayWeekStart)}
                        className="min-h-[44px] px-4 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 text-[11px] uppercase tracking-wider font-bold transition-all"
                    >
                        Hoje
                    </button>
                    <button
                        data-testid="agenda-next-week"
                        onClick={() => goWeek(1)}
                        disabled={!canNext}
                        className="min-h-[44px] h-11 w-11 rounded-xl border border-amber-500/30 hover:border-amber-500/60 text-amber-200 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                        title="Próxima semana"
                    >
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>
                </div>
                <div className="text-center min-w-0">
                    <div className="gold-text text-lg salon-heading" data-testid="agenda-range-label">{rangeLabel}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-0.5">
                        {weekOffsetDays === 0 ? "Semana atual" : weekOffsetDays > 0 ? `+${Math.round(weekOffsetDays / 7)} semana(s)` : `${Math.round(weekOffsetDays / 7)} semana(s)`}
                    </div>
                </div>
                <div className="text-[10px] uppercase tracking-wider text-amber-200/50">
                    Navegação ±6 meses
                </div>
            </div>

            {/* Grid semanal */}
            <DndContext
                sensors={sensors}
                onDragOver={handleDragOver}
                onDragEnd={(e) => { setConflictPreviewIdx(null); handleDragEnd(e); }}
                onDragCancel={() => setConflictPreviewIdx(null)}
            >
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="agenda-week-grid">
                    {days.map((d, idx) => {
                        const isToday = d.getTime() === today.getTime();
                        const isPast = d.getTime() < today.getTime();
                        const appts = appointmentsByDay[idx];
                        return (
                            <DroppableDay
                                key={idx}
                                idx={idx}
                                date={d}
                                isToday={isToday}
                                isPast={isPast}
                                appts={appts}
                                isConflict={conflictPreviewIdx === idx}
                                onOpenEdit={openEdit}
                                onAddNew={() => openNew(d)}
                            />
                        );
                    })}
                </div>
            </DndContext>

            {/* Modal de edição */}
            {editing && (
                <AppointmentModal
                    appt={editing}
                    setAppt={setEditing}
                    onClose={() => setEditing(null)}
                    onSave={handleSave}
                    onDelete={handleDelete}
                />
            )}

            {/* Modal de resolução de conflito */}
            {conflict && (
                <ConflictResolutionModal
                    conflict={conflict}
                    currentPro={pro}
                    onClose={() => setConflict(null)}
                    onPickSlot={handleConflictPickSlot}
                    onPickPro={handleConflictPickPro}
                />
            )}
        </div>
    );
};

// ─── Droppable Day Column ──────────────────────────────────────────────
const DroppableDay = ({ idx, date, isToday, isPast, appts, isConflict, onOpenEdit, onAddNew }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: `day-${idx}`,
        data: { kind: "day", dayIdx: idx, dateMs: date.getTime() },
    });
    const showConflict = isOver && isConflict;
    return (
        <div
            ref={setNodeRef}
            data-testid={`agenda-day-${idx}`}
            data-day-over={isOver ? "true" : "false"}
            data-conflict={showConflict ? "true" : "false"}
            className={`rounded-xl border bg-black/40 p-3 flex flex-col min-h-[260px] transition-all relative ${
                showConflict
                    ? "border-red-500/70 bg-red-500/5 shadow-[0_0_0_2px_rgba(239,68,68,0.55)] animate-pulse"
                    : isOver
                        ? "border-amber-300 bg-amber-500/10 shadow-[0_0_0_2px_rgba(212,175,55,0.55)]"
                        : isToday
                            ? "border-amber-400/70 shadow-[0_0_24px_-12px_rgba(212,175,55,0.6)]"
                            : "border-amber-500/15 hover:border-amber-500/30"
            }`}
        >
            {showConflict && (
                <div className="absolute top-2 right-2 z-10 px-2 py-1 rounded-md bg-red-500/20 border border-red-500/50 text-red-300 text-[10px] uppercase tracking-wider font-bold pointer-events-none">
                    <i className="fa-solid fa-triangle-exclamation mr-1"></i>
                    Conflito
                </div>
            )}
            <header className="flex items-baseline justify-between pb-2 border-b border-amber-500/15">
                <span className={`text-[10px] uppercase tracking-[0.25em] font-bold ${isToday ? "text-amber-300" : "text-amber-200/60"}`}>
                    {WEEKDAYS[idx]}
                </span>
                <span className={`salon-heading text-xl ${isPast ? "text-amber-200/40" : isToday ? "text-amber-300" : "text-amber-100"}`}>
                    {String(date.getDate()).padStart(2, "0")}
                </span>
            </header>
            <div className="flex-1 space-y-1.5 mt-2 overflow-y-auto max-h-[300px]">
                {appts.length === 0 && (
                    <div className="text-center text-amber-200/25 text-[10px] py-4 italic">
                        Livre
                    </div>
                )}
                {appts.map((a) => (
                    <DraggableApptCard key={a.id} appt={a} onOpenEdit={onOpenEdit} />
                ))}
            </div>
            <button
                data-testid={`agenda-add-${idx}`}
                onClick={onAddNew}
                className="mt-2 min-h-[36px] rounded-lg border border-dashed border-amber-500/30 hover:border-amber-500/60 hover:bg-amber-500/5 text-amber-300/70 hover:text-amber-200 text-[10px] uppercase tracking-wider font-bold transition-all"
            >
                <i className="fa-solid fa-plus mr-1"></i>Novo
            </button>
        </div>
    );
};

// ─── Draggable Appointment Card ────────────────────────────────────────
const DraggableApptCard = ({ appt, onOpenEdit }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: appt.id,
        data: { kind: "appt" },
    });
    const st = STATUS_STYLES[appt.status] || STATUS_STYLES.agendado;
    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 50 : "auto",
        cursor: isDragging ? "grabbing" : "grab",
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`agenda-appt-${appt.id}`}
            data-dragging={isDragging ? "true" : "false"}
            {...listeners}
            {...attributes}
            onClick={() => !isDragging && onOpenEdit(appt)}
            className={`select-none w-full text-left rounded-lg border ${st.border} ${st.bg} ${st.text} px-2 py-1.5 hover:brightness-125 transition-[filter,box-shadow] ${isDragging ? "ring-2 ring-amber-300 shadow-2xl" : ""}`}
            role="button"
            tabIndex={0}
        >
            <div className="flex items-center gap-1.5">
                <i className="fa-solid fa-grip-vertical text-[9px] opacity-40"></i>
                <i className="fa-solid fa-clock text-[9px] opacity-70"></i>
                <span className="font-mono text-[11px] font-bold">{appt.time}</span>
            </div>
            <div className="text-[11px] font-semibold truncate mt-0.5">{appt.client}</div>
            <div className="text-[10px] opacity-70 truncate">{appt.service}</div>
        </div>
    );
};


const AppointmentModal = ({ appt, setAppt, onClose, onSave, onDelete }) => {
    const dateLabel = new Date(appt.date).toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
    });
    return (
        <div
            data-testid="agenda-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <div className="gold-panel max-w-md w-full p-5 lg:p-6 space-y-4">
                <header className="flex items-start justify-between gap-3 pb-3 gold-border-bottom">
                    <div>
                        <h3 className="gold-text salon-heading text-lg">
                            {appt.id ? "Editar Agendamento" : "Novo Agendamento"}
                        </h3>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/60 mt-1 capitalize">
                            {dateLabel}
                        </div>
                    </div>
                    <button
                        data-testid="agenda-modal-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-lg border border-amber-500/25 hover:border-amber-500/60 text-amber-200 transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                <GoldField label="Horário" testId="agenda-field-time">
                    <input
                        type="time"
                        value={appt.time}
                        onChange={(e) => setAppt({ ...appt, time: e.target.value })}
                        className={`${goldInputCls} font-mono`}
                    />
                </GoldField>
                <GoldField label="Cliente" testId="agenda-field-client">
                    <input
                        value={appt.client}
                        onChange={(e) => setAppt({ ...appt, client: e.target.value })}
                        placeholder="Nome do cliente"
                        className={goldInputCls}
                    />
                </GoldField>
                <GoldField label="Serviço" testId="agenda-field-service">
                    <input
                        value={appt.service}
                        onChange={(e) => setAppt({ ...appt, service: e.target.value })}
                        placeholder="Corte, Barba, Hidratação…"
                        className={goldInputCls}
                    />
                </GoldField>
                <GoldField label="Status" testId="agenda-field-status">
                    <div className="grid grid-cols-3 gap-2">
                        {Object.entries(STATUS_STYLES).map(([id, st]) => {
                            const active = appt.status === id;
                            return (
                                <button
                                    key={id}
                                    data-testid={`agenda-status-${id}`}
                                    onClick={() => setAppt({ ...appt, status: id })}
                                    className={`min-h-[40px] rounded-lg border px-2 text-[10px] uppercase tracking-wider font-bold transition-all ${
                                        active
                                            ? `${st.border} ${st.bg} ${st.text}`
                                            : "border-amber-500/15 bg-black/40 text-amber-200/50 hover:border-amber-500/40"
                                    }`}
                                >
                                    {st.label}
                                </button>
                            );
                        })}
                    </div>
                </GoldField>

                <footer className="flex items-center justify-between gap-2 pt-2">
                    {appt.id ? (
                        <button
                            data-testid="agenda-modal-delete"
                            onClick={onDelete}
                            className="min-h-[44px] h-11 w-11 rounded-xl border border-red-500/30 hover:border-red-500/60 text-red-400 transition-all"
                            title="Remover"
                        >
                            <i className="fa-solid fa-trash text-xs"></i>
                        </button>
                    ) : (
                        <span />
                    )}
                    <div className="flex items-center gap-2">
                        <button
                            data-testid="agenda-modal-cancel"
                            onClick={onClose}
                            className="min-h-[44px] px-4 rounded-xl border border-amber-500/20 hover:border-amber-500/45 text-amber-200/80 text-[11px] uppercase tracking-wider font-bold transition-all"
                        >
                            Cancelar
                        </button>
                        <button
                            data-testid="agenda-modal-save"
                            onClick={onSave}
                            className="gold-pill gold-pill-active min-h-[44px] px-5 rounded-xl text-[11px] font-bold uppercase tracking-[0.15em]"
                        >
                            <i className="fa-solid fa-floppy-disk mr-2"></i>Salvar
                        </button>
                    </div>
                </footer>
            </div>
        </div>
    );
};

// ─── Reusable ───────────────────────────────────────────────────────────────
const goldInputCls =
    "w-full bg-black/60 border border-amber-500/22 rounded-xl px-3.5 py-2.5 text-amber-100 text-sm outline-none focus:border-amber-400/55 transition-colors min-h-[44px] placeholder:text-amber-200/30";

const GoldField = ({ label, testId, children }) => (
    <label className="block" data-testid={testId}>
        <span className="text-[11px] tracking-[0.15em] text-amber-300/80 font-semibold">{label}</span>
        <div className="mt-1.5">{children}</div>
    </label>
);

const GoldKpi = ({ label, value, icon, highlight }) => (
    <div
        className={`gold-panel p-4 ${
            highlight ? "ring-1 ring-emerald-400/40 shadow-[0_0_30px_-15px_rgba(16,185,129,0.5)]" : ""
        }`}
    >
        <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70">{label}</div>
            <div className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-500/15 text-amber-300">
                <i className={`fa-solid ${icon} text-sm`}></i>
            </div>
        </div>
        <div
            className="salon-heading text-2xl mt-2"
            style={{ color: highlight ? "#34d399" : "#fde7a8" }}
        >
            {value}
        </div>
    </div>
);

const Toggle = ({ checked, onChange, testId }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        data-testid={testId}
        onClick={onChange}
        className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors min-h-[28px] ${
            checked ? "bg-amber-500" : "bg-slate-700"
        }`}
    >
        <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
                checked ? "translate-x-6" : "translate-x-1"
            }`}
        />
    </button>
);

// ─── New Collaborator Modal ──────────────────────────────────────────────
const NewCollaboratorModal = ({ open, onClose, onCreate }) => {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: "",
        role: "",
        phone: "",
        email: "",
        pixKey: "",
        avatar: "",
        commissionRate: 40,
        fixedSalaryActive: false,
        fixedSalaryValue: 0,
        commissionActive: true,
    });

    if (!open) return null;

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const handleSubmit = async (e) => {
        e?.preventDefault();
        if (saving) return;
        if (!form.name.trim()) {
            toast.error("Informe o nome do colaborador.");
            return;
        }
        if (!form.role.trim()) {
            toast.error("Informe o cargo do colaborador.");
            return;
        }
        if (!form.fixedSalaryActive && !form.commissionActive) {
            toast.error("Ative ao menos um modo de ganho (comissão ou salário fixo).");
            return;
        }
        setSaving(true);
        try {
            await onCreate({
                name: form.name.trim(),
                role: form.role.trim(),
                phone: form.phone.trim(),
                email: form.email.trim().toLowerCase(),
                pixKey: form.pixKey.trim(),
                avatar: form.avatar || null,
                fixedSalary: {
                    active: form.fixedSalaryActive,
                    value: Number(form.fixedSalaryValue) || 0,
                },
                commission: {
                    active: form.commissionActive,
                    rate: Number(form.commissionRate) || 0,
                },
            });
            // Reset
            setForm({
                name: "",
                role: "",
                phone: "",
                email: "",
                pixKey: "",
                avatar: "",
                commissionRate: 40,
                fixedSalaryActive: false,
                fixedSalaryValue: 0,
                commissionActive: true,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            data-testid="new-collab-modal"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
            onClick={(e) => e.target === e.currentTarget && onClose()}
        >
            <form
                onSubmit={handleSubmit}
                className="rounded-2xl border border-amber-500/30 bg-slate-950 max-w-xl w-full p-6 space-y-5 max-h-[90vh] overflow-y-auto"
            >
                <header className="flex items-start justify-between pb-3 border-b border-white/5">
                    <div>
                        <h3 className="salon-heading text-xl text-amber-200">Novo Colaborador</h3>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mt-1">
                            Cadastro rápido · todos os campos exceto e-mail são obrigatórios
                        </p>
                    </div>
                    <button
                        type="button"
                        data-testid="new-collab-close"
                        onClick={onClose}
                        className="h-9 w-9 rounded-lg border border-white/10 hover:border-white/30 text-slate-400 hover:text-white transition-all"
                    >
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </header>

                <div className="space-y-3">
                    <AvatarUploadField
                        avatar={form.avatar}
                        name={form.name}
                        onChange={(v) => setField("avatar", v)}
                        testId="new-collab-avatar"
                    />
                    <Field label="Nome Completo">
                        <input
                            data-testid="new-collab-name"
                            autoFocus
                            value={form.name}
                            onChange={(e) => setField("name", e.target.value)}
                            placeholder="Ex.: João Silva"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </Field>
                    <Field label="Cargo">
                        <input
                            data-testid="new-collab-role"
                            value={form.role}
                            onChange={(e) => setField("role", e.target.value)}
                            placeholder="Ex.: Barbeiro Master"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Telefone">
                            <input
                                data-testid="new-collab-phone"
                                value={form.phone}
                                onChange={(e) => setField("phone", maskPhone(e.target.value))}
                                placeholder="(11) 98765-4321"
                                className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60 font-mono"
                            />
                        </Field>
                        <Field label="Chave PIX">
                            <input
                                data-testid="new-collab-pix"
                                value={form.pixKey}
                                onChange={(e) => setField("pixKey", e.target.value)}
                                placeholder="email@ou.cpf"
                                className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                            />
                        </Field>
                    </div>
                    <Field label="E-mail (acesso pessoal ao app)">
                        <input
                            data-testid="new-collab-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => setField("email", e.target.value)}
                            placeholder="opcional — colaborador@email.com"
                            className="w-full min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3.5 text-sm text-white outline-none focus:border-amber-400/60"
                        />
                        <p className="text-[11px] text-slate-500 mt-1">
                            Com e-mail + telefone preenchidos, o colaborador recebe uma senha temporária por WhatsApp e passa a poder entrar no app para ver a própria agenda e comissão.
                        </p>
                    </Field>

                    {/* Ganhos */}
                    <div className="rounded-xl border border-amber-500/15 bg-black/30 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-percent text-amber-400 text-xs"></i>
                                <span className="text-xs font-bold uppercase tracking-wider text-amber-200">
                                    Comissão por atendimento
                                </span>
                            </div>
                            <Toggle
                                testId="new-collab-toggle-commission"
                                checked={form.commissionActive}
                                onChange={() => setField("commissionActive", !form.commissionActive)}
                            />
                        </div>
                        {form.commissionActive && (
                            <div className="flex items-center gap-3">
                                <input
                                    data-testid="new-collab-commission-rate"
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={form.commissionRate}
                                    onChange={(e) => setField("commissionRate", e.target.value)}
                                    className="w-24 min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3 text-sm text-white outline-none focus:border-amber-400/60 font-mono"
                                />
                                <span className="text-xs text-slate-400">% sobre cada serviço/produto</span>
                            </div>
                        )}
                    </div>

                    <div className="rounded-xl border border-amber-500/15 bg-black/30 p-3 space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <i className="fa-solid fa-money-bill-wave text-amber-400 text-xs"></i>
                                <span className="text-xs font-bold uppercase tracking-wider text-amber-200">
                                    Salário fixo mensal
                                </span>
                            </div>
                            <Toggle
                                testId="new-collab-toggle-fixed"
                                checked={form.fixedSalaryActive}
                                onChange={() => setField("fixedSalaryActive", !form.fixedSalaryActive)}
                            />
                        </div>
                        {form.fixedSalaryActive && (
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-slate-400">R$</span>
                                <input
                                    data-testid="new-collab-fixed-value"
                                    type="number"
                                    min="0"
                                    value={form.fixedSalaryValue}
                                    onChange={(e) => setField("fixedSalaryValue", e.target.value)}
                                    className="w-32 min-h-[44px] rounded-xl bg-black/60 border border-amber-500/20 px-3 text-sm text-white outline-none focus:border-amber-400/60 font-mono"
                                />
                                <span className="text-xs text-slate-400">/ mês</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="min-h-[44px] px-4 rounded-xl border border-white/10 hover:border-white/30 text-slate-300 text-[11px] uppercase tracking-wider font-bold"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        data-testid="new-collab-save"
                        disabled={saving}
                        className="min-h-[44px] px-5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 text-[11px] uppercase tracking-wider font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-user-plus"} mr-2`}></i>
                        {saving ? "Salvando…" : "Cadastrar Colaborador"}
                    </button>
                </div>
            </form>
        </div>
    );
};

const Field = ({ label, children }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-[0.2em] text-amber-200/80 mb-1.5 font-bold">
            {label}
        </span>
        {children}
    </label>
);

export default Collaborators;
